import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useStoreLocation } from '../context/StoreLocation';
import { useAuthState } from '../context/UserAuthentication';
import { useAppSelector } from '../redux/store/store';
import { pulseGetCart } from './pulseGetCart';
import {
  buildPulseGetCartPayload,
  disarmTapCartIconClick,
  isTapCartIconClickPending,
  resolveTapCartIconPagePath,
  snapshotFromCartApiResponse,
  trackTapCartIcon,
} from './pulseTapCartIcon';

/**
 * Fire `tap_cart_icon` once per cart open after analytics get-cart resolves.
 * Isolated from product cart sync (own axios read, not orderMicroService.cancel).
 */
export const useTrackTapCartIconOnCartOpen = (isCartOpen: boolean) => {
  const location = useLocation();
  const { authInfo } = useAuthState();
  const { locationInfo } = useStoreLocation();
  const orderType = useAppSelector((state) => state.cart?.orderType);
  const rewardsHeaderClickCategory = useAppSelector(
    (state) => state.Items.extras.rewardsHeaderClickCategory
  );
  const trackedRef = useRef(false);
  const locationId = locationInfo?.selectedStore?.id;

  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  const rewardsCategoryRef = useRef(rewardsHeaderClickCategory);
  rewardsCategoryRef.current = rewardsHeaderClickCategory;
  const orderTypeRef = useRef(orderType);
  orderTypeRef.current = orderType;

  useEffect(() => {
    if (!isCartOpen) {
      trackedRef.current = false;
      disarmTapCartIconClick();
      return;
    }
    if (trackedRef.current || !authInfo.userId || !locationId) return;
    if (!isTapCartIconClickPending()) return;

    let cancelled = false;

    const run = async () => {
      try {
        const payload = buildPulseGetCartPayload({
          locationId,
          customerId: authInfo.userId,
          orderType: orderTypeRef.current,
        });
        const response = await pulseGetCart(payload);
        if (cancelled || trackedRef.current) return;
        trackedRef.current = true;
        disarmTapCartIconClick();
        const pagePath = resolveTapCartIconPagePath(pathnameRef.current);
        trackTapCartIcon({
          pathname: pagePath,
          rewardsCategory: rewardsCategoryRef.current,
          cart: snapshotFromCartApiResponse(response),
          page_path: pagePath,
        });
      } catch (_) {
        // Analytics-only read; never affect cart UX.
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [isCartOpen, authInfo.userId, locationId]);
};
